import React, { useState, useEffect, useMemo } from "react";
import { collection, query, orderBy, onSnapshot, doc, getDocs, updateDoc, deleteDoc, getDocs as getAllDocs } from "firebase/firestore";
import { db } from "../firebase";
import "./AdminChatList.css";
import { 
  FaSearch, 
  FaUser, 
  FaCheck, 
  FaCheckDouble, 
  FaMicrophone,
  FaFilter,
  FaSortAmountDown,
  FaSortAmountUp,
  FaTimes,
  FaBell,
  FaComments,
  FaTrash
} from "react-icons/fa";

const AdminChatList = ({ onSelectChat }) => {
  const [chats, setChats] = useState([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, unread, read
  const [sortBy, setSortBy] = useState("recent"); // recent, name, unread
  const [sortOrder, setSortOrder] = useState("desc"); // asc, desc
  const [loading, setLoading] = useState(true);
  const [hoveredChatId, setHoveredChatId] = useState(null);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  useEffect(() => {
    const chatCollection = collection(db, "chats");

    const unsubscribe = onSnapshot(chatCollection, async (snapshot) => {
      setLoading(true);
      const chatData = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const chat = { id: docSnap.id, ...docSnap.data() };

          // Fetch last message from messages subcollection
          const messagesRef = collection(db, "chats", chat.id, "messages");
          const messagesQuery = query(messagesRef, orderBy("timestamp", "desc"));
          const messagesSnapshot = await getDocs(messagesQuery);
          const lastMessageDoc = messagesSnapshot.docs[0];
          const lastMessageData = lastMessageDoc ? lastMessageDoc.data() : null;

          return {
            ...chat,
            lastMessage: lastMessageData?.text || (lastMessageData?.messageType === "voice" ? "🎤 رسالة صوتية" : "لا توجد رسائل"),
            lastMessageType: lastMessageData?.messageType || "text",
            lastMessageTimestamp: lastMessageData?.timestamp || null,
            unread: chat.unread || 0,
          };
        })
      );

      setChats(chatData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Format timestamp to relative time
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return "الآن";
    if (minutes < 60) return `منذ ${minutes} دقيقة`;
    if (hours < 24) return `منذ ${hours} ساعة`;
    if (days < 7) return `منذ ${days} يوم`;
    
    // Format full date for older messages - Gregorian calendar (Algerian format with French-based months in Arabic)
    const arabicMonths = [
      'جانفي', 'فيفري', 'مارس', 'أفريل', 'ماي', 'جوان',
      'جويلية', 'أوت', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
    ];
    
    const day = date.getDate();
    const month = arabicMonths[date.getMonth()];
    const year = date.getFullYear();
    const dateHours = date.getHours();
    const dateMinutes = date.getMinutes();
    const ampm = dateHours >= 12 ? 'م' : 'ص';
    const displayHours = dateHours % 12 || 12;
    const displayMinutes = dateMinutes.toString().padStart(2, '0');
    
    return `${day} ${month} ${year}، ${displayHours}:${displayMinutes} ${ampm}`;
  };

  // Get customer initials for avatar
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Truncate message preview
  const truncateMessage = (text, maxLength = 50) => {
    if (!text) return "لا توجد رسائل";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Filter and sort chats
  const filteredAndSortedChats = useMemo(() => {
    let filtered = chats.filter((chat) => {
      const matchesSearch = chat.customerName?.toLowerCase().includes(search.toLowerCase()) ||
                          chat.lastMessage?.toLowerCase().includes(search.toLowerCase());
      
      if (!matchesSearch) return false;

      if (filterType === "unread") return chat.unread > 0;
      if (filterType === "read") return chat.unread === 0;
      return true;
    });

    // Sort chats
    filtered.sort((a, b) => {
      if (sortBy === "name") {
        const nameA = (a.customerName || "").toLowerCase();
        const nameB = (b.customerName || "").toLowerCase();
        return sortOrder === "asc" 
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }
      
      if (sortBy === "unread") {
        return sortOrder === "asc"
          ? a.unread - b.unread
          : b.unread - a.unread;
      }

      // Sort by recent (default)
      const timeA = a.lastMessageTimestamp?.toDate ? a.lastMessageTimestamp.toDate().getTime() : 0;
      const timeB = b.lastMessageTimestamp?.toDate ? b.lastMessageTimestamp.toDate().getTime() : 0;
      return sortOrder === "asc" ? timeA - timeB : timeB - timeA;
    });

    return filtered;
  }, [chats, search, filterType, sortBy, sortOrder]);

  // Mark chat as read when clicked
  const handleSelectChat = async (chatId, customerName) => {
    const chatRef = doc(db, "chats", chatId);
    await updateDoc(chatRef, { unread: false }); // Set unread to false

    setChats((prevChats) =>
      prevChats.map((chat) =>
        chat.id === chatId ? { ...chat, unread: 0 } : chat
      )
    );

    onSelectChat(chatId, customerName);
  };

  // Mark all as read
  const markAllAsRead = async () => {
    const unreadChats = chats.filter(chat => chat.unread > 0);
    await Promise.all(
      unreadChats.map(chat => 
        updateDoc(doc(db, "chats", chat.id), { unread: false })
      )
    );
  };

  // Delete all chats
  const deleteAllChats = async () => {
    if (!showDeleteAllConfirm) {
      setShowDeleteAllConfirm(true);
      return;
    }

    setIsDeletingAll(true);
    try {
      // Get all chats and their messages
      const chatDocs = await getAllDocs(collection(db, "chats"));
      
      // Delete all messages for each chat, then delete the chat itself
      const deletePromises = [];
      
      for (const chatDoc of chatDocs.docs) {
        const chatId = chatDoc.id;
        
        // Get all messages in this chat
        const messagesRef = collection(db, "chats", chatId, "messages");
        const messagesSnapshot = await getAllDocs(messagesRef);
        
        // Delete all messages
        messagesSnapshot.docs.forEach(messageDoc => {
          deletePromises.push(deleteDoc(doc(db, "chats", chatId, "messages", messageDoc.id)));
        });
        
        // Delete the chat document
        deletePromises.push(deleteDoc(doc(db, "chats", chatId)));
      }
      
      await Promise.all(deletePromises);
      setShowDeleteAllConfirm(false);
      alert("تم حذف جميع الدردشات بنجاح");
    } catch (error) {
      console.error("Error deleting all chats:", error);
      alert("حدث خطأ أثناء حذف الدردشات");
    } finally {
      setIsDeletingAll(false);
    }
  };

  // Statistics
  const stats = useMemo(() => {
    const total = chats.length;
    const unread = chats.filter(chat => chat.unread > 0).length;
    const read = total - unread;
    return { total, unread, read };
  }, [chats]);

  return (
    <div className="admin-chat-container">
      {/* Header */}
      <div className="admin-chat-header">
        <div className="header-content">
          <div className="header-icon">
            <FaComments size={24} />
          </div>
          <div>
            <h2 className="header-title">لوحة تحكم الدردشات</h2>
            <p className="header-subtitle">إدارة جميع المحادثات مع العملاء</p>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-container">
        <div className="stat-card stat-total">
          <div className="stat-icon">
            <FaComments size={20} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">إجمالي الدردشات</div>
          </div>
        </div>
        <div className="stat-card stat-unread">
          <div className="stat-icon">
            <FaBell size={20} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.unread}</div>
            <div className="stat-label">غير مقروء</div>
          </div>
        </div>
        <div className="stat-card stat-read">
          <div className="stat-icon">
            <FaCheckDouble size={20} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.read}</div>
            <div className="stat-label">مقروء</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="controls-container">
        <div className="search-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="🔍 ابحث عن اسم العميل أو الرسالة..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          {search && (
            <button 
              className="clear-search-btn"
              onClick={() => setSearch("")}
              title="مسح البحث"
            >
              <FaTimes size={12} />
            </button>
          )}
        </div>

        <div className="filters-container">
          <div className="filter-group">
            <FaFilter className="filter-icon" />
            <select 
              className="filter-select"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">الكل</option>
              <option value="unread">غير مقروء فقط</option>
              <option value="read">مقروء فقط</option>
            </select>
          </div>

          <div className="filter-group">
            {sortOrder === "desc" ? (
              <FaSortAmountDown className="filter-icon" />
            ) : (
              <FaSortAmountUp className="filter-icon" />
            )}
            <select 
              className="filter-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="recent">الأحدث</option>
              <option value="name">الاسم</option>
              <option value="unread">عدد غير المقروء</option>
            </select>
            <button 
              className="sort-order-btn"
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              title={sortOrder === "asc" ? "ترتيب تنازلي" : "ترتيب تصاعدي"}
            >
              {sortOrder === "asc" ? "↑" : "↓"}
            </button>
          </div>

          {stats.unread > 0 && (
            <button 
              className="mark-all-read-btn"
              onClick={markAllAsRead}
              title="تعليم الكل كمقروء"
            >
              <FaCheckDouble size={14} />
              تعليم الكل كمقروء
            </button>
          )}
          
          {chats.length > 0 && (
            <button 
              className="delete-all-btn"
              onClick={deleteAllChats}
              disabled={isDeletingAll}
              title={showDeleteAllConfirm ? "تأكيد الحذف" : "حذف الكل"}
            >
              <FaTrash size={14} />
              {showDeleteAllConfirm ? "تأكيد حذف الكل" : isDeletingAll ? "جاري الحذف..." : "حذف الكل"}
            </button>
          )}
          
          {showDeleteAllConfirm && (
            <button 
              className="cancel-delete-btn"
              onClick={() => setShowDeleteAllConfirm(false)}
              title="إلغاء"
            >
              <FaTimes size={14} />
              إلغاء
            </button>
          )}
        </div>
      </div>

      {/* Chat List */}
      <div className="chat-list-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>جاري تحميل الدردشات...</p>
          </div>
        ) : filteredAndSortedChats.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">💬</div>
            <h3>لا توجد دردشات</h3>
            <p>
              {search 
                ? "لم يتم العثور على دردشات تطابق البحث"
                : filterType === "unread"
                ? "لا توجد رسائل غير مقروءة"
                : "لا توجد دردشات متاحة حالياً"}
            </p>
          </div>
        ) : (
          <div className="chat-list">
            {filteredAndSortedChats.map((chat) => (
              <div
                key={chat.id}
                className={`chat-item ${chat.unread > 0 ? "unread" : ""} ${hoveredChatId === chat.id ? "hovered" : ""}`}
                onClick={() => handleSelectChat(chat.id, chat.customerName)}
                onMouseEnter={() => setHoveredChatId(chat.id)}
                onMouseLeave={() => setHoveredChatId(null)}
              >
                <div className="chat-avatar">
                  <FaUser size={18} />
                  {chat.unread > 0 && (
                    <div className="unread-badge">{chat.unread > 99 ? "99+" : chat.unread}</div>
                  )}
                </div>
                
                <div className="chat-content">
                  <div className="chat-header">
                    <div className="chat-name">
                      {chat.customerName || "مستخدم غير معروف"}
                    </div>
                    <div className="chat-time">
                      {formatTimestamp(chat.lastMessageTimestamp)}
                    </div>
                  </div>
                  
                  <div className="chat-preview">
                    {chat.lastMessageType === "voice" ? (
                      <span className="voice-message-preview">
                        <FaMicrophone size={12} /> رسالة صوتية
                      </span>
                    ) : (
                      <span className="text-message-preview">
                        {truncateMessage(chat.lastMessage)}
                      </span>
                    )}
                    {chat.unread > 0 && (
                      <span className="unread-indicator"></span>
                    )}
                  </div>
                </div>

                <div className="chat-status">
                  {chat.unread > 0 ? (
                    <div className="status-unread">
                      <FaBell size={14} />
                    </div>
                  ) : (
                    <div className="status-read">
                      <FaCheckDouble size={14} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminChatList;
